import { can, clientName, formatDate } from '@clim/client-core';
import { COMPLIANCE_LABELS, interventionMachine, photoOriginOf, type ComplianceStatus, type InterventionRecord, type InterventionStatus } from '@clim/domain';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { errorMessage, fieldErrors } from '../lib/errors';
import { useEngine, useReady } from '../lib/session';
import { useOnline } from '../lib/use-online';
import { InstallPhotosDialog } from './DossierFiles';
import { Empty, ErrorBox, Field, Input, Modal, Select, Textarea } from './ui';

const TONE: Record<InterventionStatus, string> = {
  to_assign: 'warning',
  proposed: 'info',
  accepted: 'info',
  scheduled: 'progress',
  taken_charge: 'progress',
  on_site: 'progress',
  completed: 'success',
  impossible: 'danger',
  cancelled: 'neutral',
};

export function InterventionBadge({ status, reassign }: { status: InterventionStatus; reassign?: boolean }) {
  return <span className={`badge tone-${TONE[status]}`}>{reassign ? 'À réattribuer' : interventionMachine.labels[status]}</span>;
}

export const fmtDay = (d: string | null) =>
  d ? new Date(`${d}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }) : 'Date à définir';
export const slot = (i: Pick<InterventionRecord, 'slotStart' | 'slotEnd'>) => (i.slotStart ? `${i.slotStart}${i.slotEnd ? `–${i.slotEnd}` : ''}` : 'Journée');

/** Motif obligatoire (listes administrables + « Autre » avec texte) — refus, impossibilité, réattribution… */
export function ReasonDialog({ title, category, confirm, onConfirm, onClose }: { title: string; category: string; confirm: string; onConfirm(reasonId: string, comment: string | null): Promise<void>; onClose(): void }) {
  const engine = useEngine();
  const reasons = engine.list('reason').filter((r) => r.category === category && r.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
  const [reasonId, setReasonId] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const r = reasons.find((x) => x.id === reasonId);
  return (
    <Modal title={title} onClose={onClose}>
      <div className="form">
        <div className="chips" role="radiogroup" aria-label="Motif">
          {reasons.map((x) => (
            <button key={x.id} type="button" role="radio" aria-checked={reasonId === x.id} className={`chip${reasonId === x.id ? ' on' : ''}`} onClick={() => setReasonId(x.id)}>
              {x.label}
            </button>
          ))}
        </div>
        <Field label={`Commentaire${r?.requiresComment ? ' (obligatoire)' : ''}`}>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} />
        </Field>
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Retour
          </button>
          <button
            className="btn primary"
            disabled={!reasonId || (!!r?.requiresComment && !comment.trim())}
            onClick={() =>
              onConfirm(reasonId, comment.trim() || null)
                .then(onClose)
                .catch((e) => setError(errorMessage(e)))
            }
          >
            {confirm}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** Carte d'intervention (planning, liste technicien, dossier). */
export function InterventionCard({ i, onOpen, children }: { i: InterventionRecord; onOpen?(): void; children?: React.ReactNode }) {
  const engine = useEngine();
  const d = engine.get('dossier', i.dossierId);
  const client = d ? engine.get('client', d.clientId) : undefined;
  const reassign = i.status === 'to_assign' && !!i.statusReasonId;
  return (
    <div className={`iv-card iv-${i.activity ?? 'pose'}`}>
      <button type="button" className="iv-main" onClick={onOpen} disabled={!onOpen}>
        <span className="iv-time">{slot(i)}</span>
        <span className="row-main">
          <strong>
            {client ? clientName(client) : 'Client'} · {i.city ?? '—'}
            {i.needsNacelle && <span title="Nacelle prévue"> · 🏗 nacelle</span>}
          </strong>
          <span className="muted">
            {d?.reference ?? 'Référence à la synchro'} · {i.technicianName ?? 'non attribuée'}
            {i.coTechnicianNames?.length ? ` + ${i.coTechnicianNames.join(', ')} (renfort)` : ''}
            {i.completedByName && i.status === 'completed' ? ` · réalisée par ${i.completedByName}` : ''}
          </span>
          {i.clientContactedAt && (
            <span className="small contacted">
              ☎ Client contacté par {i.clientContactedByName ?? 'le technicien'} le {formatDate(i.clientContactedAt)} — rendez-vous fixé par le poseur
            </span>
          )}
          {d?.partnerId && <span className="badge partner">Partenaire : {engine.get('reason', d.partnerId)?.label ?? '—'}</span>}
          {['on_site', 'completed'].includes(i.status) && installPhotoCount(engine, i.dossierId) > 0 && (
            <span className="badge tone-info">📷 {installPhotoCount(engine, i.dossierId)} photo(s) de pose</span>
          )}
        </span>
        <InterventionBadge status={i.status} reassign={reassign} />
        {engine.syncState('intervention', i.id) !== 'synced' && <span className="sync-tag">⟳</span>}
      </button>
      {children}
    </div>
  );
}

/** Actions du planificateur sur une intervention (attribution, planification, réattribution…). */
export function InterventionDialog({ id, onClose }: { id: string; onClose(): void }) {
  const engine = useEngine();
  const { me, api } = useReady();
  const online = useOnline();
  const i = engine.get('intervention', id);
  const [techs, setTechs] = useState<{ id: string; name: string; company?: string | null; territoryIds: string[]; documents: ComplianceStatus | null }[]>([]);
  const [technicianId, setTechnicianId] = useState('');
  // techniciens en renfort choisis dès la proposition (intervention à plusieurs)
  const [helpers, setHelpers] = useState<string[]>([]);
  const [photosOpen, setPhotosOpen] = useState(false);
  const [day, setDay] = useState(i?.scheduledDate ?? '');
  const [start, setStart] = useState(i?.slotStart ?? '');
  const [end, setEnd] = useState(i?.slotEnd ?? '');
  const [reason, setReason] = useState<{ to: InterventionStatus; category: string; title: string } | null>(null);
  const [history, setHistory] = useState<{ statuses: { id: string; to: string; at: string; by: string | null; technician: string | null; reason: string | null; comment: string | null }[]; technicians: { id: string; name: string; from: string; to: string | null; endReason: string | null }[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const canPlan = can(me, 'planning.manage');
  useEffect(() => {
    if (!online || !i) return;
    if (canPlan) api.request<typeof techs>('GET', `/v1/planning/technicians?territoryId=${i.territoryId}`).then(setTechs, () => setTechs([]));
    api.request<NonNullable<typeof history>>('GET', `/v1/interventions/${id}/history`).then(setHistory, () => setHistory(null));
  }, [api, canPlan, id, online, i?.territoryId, i?.version]);
  if (!i) return null;
  const d = engine.get('dossier', i.dossierId);
  const run = (p: Promise<unknown>) => {
    setError(null);
    setFields({});
    return p.catch((e) => {
      setError(errorMessage(e));
      setFields(fieldErrors(e));
    });
  };
  const times = { scheduledDate: day || null, slotStart: start || null, slotEnd: end || null };
  const editable = canPlan && !['completed', 'cancelled'].includes(i.status);

  return (
    <Modal title={`Intervention — ${d?.reference ?? ''}`} onClose={onClose}>
      <InterventionCard i={i} />
      {d && (
        <p className="actions wrap">
          <Link to={`/dossiers/${d.id}`}>Ouvrir le dossier →</Link>
          <button type="button" className="btn small" onClick={() => setPhotosOpen(true)}>
            📷 Voir les photos de la pose ({installPhotoCount(engine, d.id)})
          </button>
        </p>
      )}
      {photosOpen && d && <InstallPhotosDialog dossierId={d.id} onClose={() => setPhotosOpen(false)} />}
      {canPlan && i.technicianId && editable && (
        <p>
          <button
            className="btn small"
            disabled={!online}
            onClick={() =>
              void run(
                api
                  .request<{ to: string }>('POST', `/v1/interventions/${i.id}/sms-reminder`, {})
                  .then((r) => alert(`SMS envoyé à ${r.to} : « consultez votre application ».`)),
              )
            }
          >
            📱 Prévenir le poseur par SMS
          </button>
        </p>
      )}
      {editable && (
        <div className="form grid3">
          <Field label="Date" error={fields.scheduledDate}>
            <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
          </Field>
          <Field label="Début">
            <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} />
          </Field>
          <Field label="Fin">
            <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
          </Field>
        </div>
      )}
      {editable && (
        <label className="check">
          <input type="checkbox" checked={i.needsNacelle} onChange={(e) => void run(engine.updateIntervention(i.id, { needsNacelle: e.target.checked }))} />
          Nacelle prévue
        </label>
      )}
      {canPlan && ['to_assign'].includes(i.status) && (
        <div className="form">
          <Field label="Technicien" error={fields.technicianId}>
            <Select aria-label="Technicien" value={technicianId} onChange={(e) => setTechnicianId(e.target.value)}>
              <option value="">— choisir —</option>
              {techs.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                  {t.company ? ` (${t.company})` : ''}
                  {t.documents && t.documents !== 'ok' ? ` — ⚠ documents : ${COMPLIANCE_LABELS[t.documents].toLowerCase()}` : ''}
                </option>
              ))}
            </Select>
          </Field>
          {technicianId && techs.length > 1 && (
            <fieldset className="crew" data-testid="crew-new">
              <legend>Renfort (intervention à plusieurs, facultatif)</legend>
              {techs
                .filter((t) => t.id !== technicianId)
                .map((t) => (
                  <label key={t.id} className="check">
                    <input
                      type="checkbox"
                      checked={helpers.includes(t.id)}
                      onChange={(e) => setHelpers(e.target.checked ? [...helpers, t.id] : helpers.filter((x) => x !== t.id))}
                    />{' '}
                    {t.name}
                    {t.company && <span className="muted"> ({t.company})</span>}
                  </label>
                ))}
              <p className="muted small">La rémunération de pose sera à répartir entre eux au moment de la valider (Commissions → Équipe).</p>
            </fieldset>
          )}
          {!online && <p className="muted">Liste des techniciens disponible en ligne.</p>}
          {(() => {
            const t = techs.find((x) => x.id === technicianId);
            return t?.documents && t.documents !== 'ok' ? (
              <p className="legal-alert small" role="status">
                ⚠ {t.name} : documents professionnels {COMPLIANCE_LABELS[t.documents].toLowerCase()}. Vous pouvez tout de même lui proposer l’intervention.
              </p>
            ) : null;
          })()}
        </div>
      )}
      {canPlan && editable && !!i.technicianId && i.status !== 'impossible' && (
        <fieldset className="crew" data-testid="crew">
          <legend>Renfort de {i.technicianName} (intervention à plusieurs)</legend>
          <p className="muted small">
            Ils voient l’intervention et le chantier. À la fin, la rémunération de pose sera à répartir entre les techniciens au
            moment de la valider (Commissions).
          </p>
          {techs
            .filter((t) => t.id !== i.technicianId)
            .map((t) => (
              <label key={t.id} className="check">
                <input
                  type="checkbox"
                  disabled={!online}
                  checked={(i.coTechnicianIds ?? []).includes(t.id)}
                  onChange={(e) => {
                    const next = e.target.checked ? [...(i.coTechnicianIds ?? []), t.id] : (i.coTechnicianIds ?? []).filter((x) => x !== t.id);
                    void run(api.request('POST', `/v1/interventions/${i.id}/crew`, { coTechnicianIds: next }).then(() => engine.sync()));
                  }}
                />{' '}
                {t.name}
                {t.company && <span className="muted"> ({t.company})</span>}
              </label>
            ))}
          {!online && <p className="muted">Modification de l’équipe disponible en ligne.</p>}
        </fieldset>
      )}
      <ErrorBox message={error} />
      <div className="actions wrap">
        {editable && (day !== (i.scheduledDate ?? '') || start !== (i.slotStart ?? '') || end !== (i.slotEnd ?? '')) && i.status !== 'to_assign' && (
          <button className="btn" onClick={() => void run(engine.updateIntervention(i.id, times))}>
            Enregistrer la date
          </button>
        )}
        {canPlan && i.status === 'to_assign' && (
          <button
            className="btn primary"
            disabled={!technicianId}
            onClick={() =>
              void run(
                engine
                  .transitionIntervention(i.id, 'proposed', { technicianId, ...times })
                  // l'intervention doit être connue du serveur avant d'y ajouter le renfort
                  .then(() => (helpers.length ? engine.sync() : undefined))
                  .then(() =>
                    helpers.length
                      ? api.request('POST', `/v1/interventions/${i.id}/crew`, { coTechnicianIds: helpers.filter((x) => x !== technicianId) }).then(() => engine.sync())
                      : undefined,
                  ),
              )
            }
          >
            Proposer au technicien{helpers.length ? ` (+ ${helpers.length} en renfort)` : ''}
          </button>
        )}
        {['proposed', 'accepted', 'scheduled', 'taken_charge', 'on_site'].includes(i.status) &&
          !!i.technicianId &&
          can(me, 'intervention.validate_for_technician') && (
            <button
              className="btn primary"
              title="Clôturer l’intervention à la place du technicien (photos reçues directement, chantier partenaire…). Sa rémunération de pose est créée comme d’habitude."
              onClick={() => {
                const comment = prompt(
                  `Terminer l’intervention pour ${i.technicianName ?? 'le technicien'} ? Il sera prévenu et sa rémunération de pose sera créée.\n\nPrécision (facultatif) :`,
                  'Photos reçues du technicien',
                );
                if (comment !== null) void run(engine.transitionIntervention(i.id, 'completed', { comment: comment.trim() || null }));
              }}
            >
              ✔ Terminer pour le technicien
            </button>
          )}
        {i.status === 'proposed' && can(me, 'intervention.validate_for_technician') && (
          <button
            className="btn"
            title="Confirmer l’intervention sans attendre que le technicien l’accepte (il est prévenu)"
            onClick={() =>
              confirm(`Valider cette intervention à la place de ${i.technicianName ?? 'du technicien'} ? Il sera prévenu.`) &&
              void run(engine.transitionIntervention(i.id, 'accepted'))
            }
          >
            ✔ Valider sans attendre le technicien
          </button>
        )}
        {canPlan && i.status === 'accepted' && (
          <button className="btn primary" onClick={() => void run(engine.transitionIntervention(i.id, 'scheduled', times))}>
            Confirmer la planification
          </button>
        )}
        {canPlan && ['proposed', 'accepted', 'scheduled'].includes(i.status) && (
          <button className="btn" onClick={() => setReason({ to: 'to_assign', category: 'intervention_reassign', title: 'Réattribuer' })}>
            Réattribuer
          </button>
        )}
        {canPlan && i.status === 'impossible' && (
          <button className="btn primary" onClick={() => void run(engine.transitionIntervention(i.id, 'to_assign'))}>
            Remettre au planning
          </button>
        )}
        {canPlan && ['to_assign', 'proposed', 'accepted', 'scheduled', 'impossible'].includes(i.status) && (
          <button className="btn danger-outline" onClick={() => setReason({ to: 'cancelled', category: 'intervention_cancel', title: 'Annuler l’intervention' })}>
            Annuler
          </button>
        )}
      </div>
      {history && (
        <details>
          <summary>Historique ({history.technicians.length} technicien(s))</summary>
          <ol className="timeline">
            {history.statuses.map((h) => (
              <li key={h.id}>
                <strong>{interventionMachine.labels[h.to as InterventionStatus]}</strong>{' '}
                <span className="muted">
                  {formatDate(h.at)} · {h.by ?? 'système'}
                  {h.technician ? ` · ${h.technician}` : ''}
                </span>
                {h.reason && <div>Motif : {h.reason}</div>}
                {h.comment && <div className="pre">{h.comment}</div>}
              </li>
            ))}
          </ol>
        </details>
      )}
      {reason && (
        <ReasonDialog
          title={reason.title}
          category={reason.category}
          confirm="Confirmer"
          onClose={() => setReason(null)}
          onConfirm={(reasonId, comment) => engine.transitionIntervention(i.id, reason.to, { reasonId, comment })}
        />
      )}
    </Modal>
  );
}

/** Interventions d'un dossier (commercial : technicien missionné / ayant réalisé ; planificateur : planifier). */
export function DossierInterventions({ dossierId }: { dossierId: string }) {
  const engine = useEngine();
  const { me } = useReady();
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const d = engine.get('dossier', dossierId);
  const list = engine
    .list('intervention')
    .filter((i) => i.dossierId === dossierId)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  if (!d || (!can(me, 'intervention.read') && !can(me, 'planning.manage') && !list.length)) return null;
  const canCreate = can(me, 'planning.manage') && ['to_schedule', 'in_progress'].includes(d.status);
  return (
    <div className="card">
      <div className="card-head">
        <h2>Interventions</h2>
        {canCreate && (
          <button
            className="btn small primary"
            onClick={() =>
              void engine
                .createIntervention(dossierId)
                .then(setOpen)
                .catch((e) => setError(errorMessage(e)))
            }
          >
            + Planifier une intervention
          </button>
        )}
      </div>
      {list.length === 0 ? <Empty>{d.status === 'to_schedule' ? 'À planifier.' : 'Aucune intervention.'}</Empty> : list.map((i) => <InterventionCard key={i.id} i={i} onOpen={() => setOpen(i.id)} />)}
      <ErrorBox message={error} />
      {open && <InterventionDialog id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

/**
 * Le poseur fixe lui-même le rendez-vous après avoir appelé le client (hors ligne possible) : date, heure, case
 * « client contacté » obligatoire. L'intervention devient « Planifiée » et le planning est prévenu.
 */
export function AppointmentDialog({ id, onClose }: { id: string; onClose(): void }) {
  const engine = useEngine();
  const i = engine.get('intervention', id);
  const [day, setDay] = useState(i?.scheduledDate ?? '');
  const [start, setStart] = useState(i?.slotStart ?? '');
  const [end, setEnd] = useState(i?.slotEnd ?? '');
  const [contacted, setContacted] = useState(false);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  if (!i) return null;
  const d = engine.get('dossier', i.dossierId);
  const c = d ? engine.get('client', d.clientId) : undefined;
  const save = () =>
    engine
      .setInterventionAppointment(i.id, { scheduledDate: day, slotStart: start, slotEnd: end || null, clientContacted: contacted, comment: comment || null })
      .then(onClose, (e) => {
        setError(errorMessage(e));
        setFields(fieldErrors(e));
      });
  return (
    <Modal title="Fixer le rendez-vous" onClose={onClose}>
      <div className="form">
        <p>
          {c ? clientName(c) : 'Client'}
          {c?.phone && (
            <>
              {' '}
              ·{' '}
              <a href={`tel:${c.phone}`} className="btn small">
                📞 {c.phone}
              </a>
            </>
          )}
        </p>
        <div className="grid3">
          <Field label="Date du rendez-vous" error={fields.scheduledDate}>
            <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
          </Field>
          <Field label="Heure d’arrivée" error={fields.slotStart}>
            <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} />
          </Field>
          <Field label="Fin (facultatif)" error={fields.slotEnd}>
            <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
          </Field>
        </div>
        <label className="check">
          <input type="checkbox" checked={contacted} onChange={(e) => setContacted(e.target.checked)} /> J’ai contacté le client, il est
          d’accord pour ce rendez-vous
        </label>
        {fields.clientContacted && <p className="field-error">{fields.clientContacted}</p>}
        <Field label="Précision (facultatif)">
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Ex. : appeler 30 min avant, portail bleu…" />
        </Field>
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Annuler
          </button>
          <button className="btn primary" disabled={!day || !start || !contacted} onClick={() => void save()}>
            Enregistrer le rendez-vous
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** nombre de photos prises par le(s) poseur(s) d'un chantier */
export function installPhotoCount(engine: ReturnType<typeof useEngine>, dossierId: string): number {
  const d = engine.get('dossier', dossierId);
  const ivs = engine.list('intervention').filter((i) => i.dossierId === dossierId);
  const commercialIds = (d?.assignments ?? []).filter((a) => a.role === 'commercial').map((a) => a.userId);
  return engine.list('dossier_file').filter((f) => f.dossierId === dossierId && f.kind === 'photo' && photoOriginOf(f, ivs, commercialIds) === 'install').length;
}
